import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    FaNetworkWired, FaExternalLinkAlt, FaSearch,
    FaCheckCircle, FaExclamationTriangle,
    FaMapMarkerAlt, FaClock, FaServer, FaWifi,
    FaChevronDown, FaChevronRight, FaSync, FaLock, FaUnlock,
    FaBuilding, FaPlusSquare, FaBell
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Empty-state shown when no device was located on any managed router.
 * This is the expected result when the user's internet service is offline
 * (the router is likely unreachable right now). We schedule a single retry
 * in 60s so the engineer doesn't have to keep clicking Refresh — if the
 * connection comes back, the diagnostics will auto-populate.
 */
const NotFoundWithRetry = ({ onRefresh }) => {
    const RETRY_SECONDS = 60;
    const [secondsLeft, setSecondsLeft] = useState(RETRY_SECONDS);
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        if (!onRefresh) return undefined;
        const tick = setInterval(() => {
            setSecondsLeft(s => {
                if (s <= 1) {
                    clearInterval(tick);
                    setRetrying(true);
                    onRefresh();
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(tick);
    }, [onRefresh]);

    return (
        <div className='diag-empty-state'>
            <FaNetworkWired size={32} />
            <p>Could not locate this device on any managed MikroTik router&apos;s DHCP leases.</p>
            <p className='diag-hint'>
                This may mean the user&apos;s internet service is currently offline, they are on a
                network not managed by CapTrac, or their device is using a static IP.
            </p>
            {onRefresh && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                    <button type='button' className='diag-action-btn' onClick={onRefresh} disabled={retrying}>
                        <FaSync size={12} /> {retrying ? 'Retrying…' : 'Retry now'}
                    </button>
                    {secondsLeft > 0 && (
                        <span className='diag-hint' style={{ margin: 0 }}>
                            Auto-retry in {secondsLeft}s
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Network Diagnostics Tab for IT Action Items.
 * Shows the MikroTik router data where the user's device was found via DHCP,
 * including the matching DHCP lease details, router info, and a link to the router config page.
 *
 * Supports inline IT-support actions:
 *   - Refresh diagnostics
 *   - Convert dynamic DHCP lease to static reservation
 *   - Release (remove) DHCP lease
 *   - Apply the matched router's property to the action item
 *   - Create a follow-up IT ticket pre-populated with the router's property
 *   - View recent RouterMonitoring alerts for the same property
 */
const NetworkDiagnosticsTab = ({ network, /*endpoint: _endpoint,*/ actionItemId, actionItemPropertyId, onRefresh }) => {
    const { apiFetch } = useAuth();
    const navigate = useNavigate();
    const [showAllLeases, setShowAllLeases] = useState(false);
    const [leaseSearch, setLeaseSearch] = useState('');
    const [busyAction, setBusyAction] = useState(null);
    const [showAlerts, setShowAlerts] = useState(false);
    const [recentAlerts, setRecentAlerts] = useState(null);
    const [loadingAlerts, setLoadingAlerts] = useState(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString();
    };

    const getMatchMethodLabel = (method) => {
        switch (method) {
            case 'MacAddress': return { label: 'MAC Address Match', icon: FaCheckCircle, color: '#38a169' };
            case 'Hostname': return { label: 'Hostname Match', icon: FaCheckCircle, color: '#3182ce' };
            case 'IpAddress': return { label: 'IP Address Match', icon: FaExclamationTriangle, color: '#dd6b20' };
            case 'RelatedEntity': return { label: 'Related Router', icon: FaNetworkWired, color: '#38a169' };
            default: return { label: 'Unknown', icon: FaExclamationTriangle, color: '#718096' };
        }
    };

    const getLeaseStatusBadge = (status) => {
        if (!status) return { color: '#718096', label: 'Unknown' };
        const s = status.toLowerCase();
        if (s === 'bound') return { color: '#38a169', label: 'Bound (Active)' };
        if (s === 'waiting') return { color: '#dd6b20', label: 'Waiting' };
        if (s === 'offered') return { color: '#3182ce', label: 'Offered' };
        return { color: '#718096', label: status };
    };

    const filteredLeases = useMemo(() => {
        if (!network?.allLeases) return [];
        const leases = network.allLeases;
        if (!leaseSearch.trim()) return leases;
        const term = leaseSearch.toLowerCase();
        return leases.filter(l =>
            l.address?.toLowerCase().includes(term) ||
            l.macAddress?.toLowerCase().includes(term) ||
            l.hostName?.toLowerCase().includes(term)
        );
    }, [network?.allLeases, leaseSearch]);

    // ------------------------------------------------------------------
    // Router Actions — inline IT-support operations
    // ------------------------------------------------------------------
    const runRouterAction = useCallback(async (key, url, body, successMsg) => {
        if (!actionItemId) return;
        setBusyAction(key);
        try {
            const res = await apiFetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body || {})
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                toast.error(data.message || 'Router action failed.');
                return;
            }
            toast.success(successMsg);
            if (onRefresh) onRefresh();
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Router action failed.');
        } finally {
            setBusyAction(null);
        }
    }, [actionItemId, apiFetch, onRefresh]);

    // Lazy-load recent alerts when the card is expanded for the first time
    useEffect(() => {
        if (!showAlerts || recentAlerts !== null || !actionItemId) return;
        let cancelled = false;
        (async () => {
            setLoadingAlerts(true);
            try {
                const params = network?.routerPropertyId
                    ? `?routerPropertyId=${network.routerPropertyId}` : '';
                const res = await apiFetch(`/api/ITDiagnostics/action-item/${actionItemId}/router-actions/recent-alerts${params}`);
                const data = await res.json();
                if (!cancelled && data.success) setRecentAlerts(data.data || []);
            } catch (err) {
                Sentry.captureException(err);
                if (!cancelled) setRecentAlerts([]);
            } finally {
                if (!cancelled) setLoadingAlerts(false);
            }
        })();
        return () => { cancelled = true; };
    }, [showAlerts, recentAlerts, actionItemId, network?.routerPropertyId, apiFetch]);

    if (!network) {
        return (
            <div className='diag-empty-state'>
                <FaNetworkWired size={32} />
                <p>No network data available. The requester&apos;s device was not found on any managed router.</p>
            </div>
        );
    }

    if (!network.found) {
        return <NotFoundWithRetry onRefresh={onRefresh} />;
    }

    const matchInfo = getMatchMethodLabel(network.matchMethod);
    const leaseStatus = getLeaseStatusBadge(network.leaseStatus);

    //
    // Derive whether the matched router is "online" using only data we already
    // have in the diagnostics payload (network.lastConnectionSuccess +
    // network.provisioningStatus). If the router hasn't checked in recently
    // we disable destructive actions so an engineer can't fire a command into
    // the void — protects against the "user's internet is down" scenario
    // where the router itself is probably unreachable right now.
    const ROUTER_FRESH_MS = 10 * 60 * 1000; // 10 minutes
    const now = useMemo(() => Date.now(), []);
    const lastConnMs = network.lastConnectionSuccess
        ? new Date(network.lastConnectionSuccess).getTime()
        : null;
    const ageMs = lastConnMs ? (now - lastConnMs) : null;
    const routerOnline = network.provisioningStatus === 'Provisioned'
        && ageMs !== null && ageMs <= ROUTER_FRESH_MS;
    const routerStaleReason = !lastConnMs
        ? 'Router has never successfully connected.'
        : ageMs > ROUTER_FRESH_MS
            ? `Router last seen ${Math.round(ageMs / 60000)} min ago — commands disabled until it reconnects.`
            : network.provisioningStatus !== 'Provisioned'
                ? `Router status is ${network.provisioningStatus}.`
                : null;

    const canAct = !!(actionItemId && network?.routerId && network?.leaseId) && routerOnline;
    const propertyMismatch = network?.routerPropertyId
        && actionItemPropertyId !== network.routerPropertyId;

    const handleMakeStatic = () => {
        if (!canAct) return;
        runRouterAction(
            'make-static',
            `/api/ITDiagnostics/action-item/${actionItemId}/router-actions/make-lease-static`,
            { routerId: network.routerId, leaseId: network.leaseId },
            'DHCP lease converted to static reservation.'
        );
    };

    const handleReleaseLease = () => {
        if (!canAct) return;
        if (!window.confirm('Release (remove) this DHCP lease? The device will request a new one on its next DHCP renewal.')) return;
        runRouterAction(
            'release',
            `/api/ITDiagnostics/action-item/${actionItemId}/router-actions/release-lease`,
            { routerId: network.routerId, leaseId: network.leaseId },
            'DHCP lease released.'
        );
    };

    const handleApplyProperty = () => {
        if (!actionItemId || !network?.routerPropertyId) return;
        runRouterAction(
            'apply-property',
            `/api/ITDiagnostics/action-item/${actionItemId}/router-actions/apply-property`,
            { routerPropertyId: network.routerPropertyId },
            'Action item property updated from matched router.'
        );
    };

    const handleCreateFollowUp = () => {
        navigate('/action-items', {
            state: {
                openCreate: true,
                selectedType: 'IT-Support-Ticket',
                preset: {
                    propertyID: network.routerPropertyId,
                    title: `Follow-up: ${network.routerDisplayName || 'Router'}`
                }
            }
        });
    };

    return (
        <div className='diag-panel network-diag-panel'>
            {/* Header with match confidence and router config link */}
            <div className='diag-header'>
                <div className='diag-header-info'>
                    <FaNetworkWired size={18} />
                    <div>
                        <h4>{network.routerDisplayName}</h4>
                        <span className='diag-subtitle'>
                            {network.routerLocation && <><FaMapMarkerAlt size={11} /> {network.routerLocation} · </>}
                            {network.routerIpAddress}
                            {network.routerOSVersion && ` · RouterOS ${network.routerOSVersion}`}
                        </span>
                    </div>
                </div>
                <div className='diag-quick-actions'>
                    <span className='diag-match-badge' style={{ color: matchInfo.color }}>
                        <matchInfo.icon size={13} /> {matchInfo.label}
                    </span>
                    {onRefresh && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            onClick={onRefresh}
                            title='Refresh diagnostics data'
                        >
                            <FaSync size={12} /> Refresh
                        </button>
                    )}
                    {canAct && network.leaseIsDynamic !== false && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            onClick={handleMakeStatic}
                            disabled={busyAction !== null}
                            title='Convert this dynamic lease to a static DHCP reservation'
                        >
                            <FaLock size={12} /> {busyAction === 'make-static' ? 'Working…' : 'Make Static'}
                        </button>
                    )}
                    {!canAct && actionItemId && network.leaseId && network.leaseIsDynamic !== false && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            disabled
                            title={routerStaleReason || 'Router unavailable.'}
                        >
                            <FaLock size={12} /> Make Static
                        </button>
                    )}
                    {canAct && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            onClick={handleReleaseLease}
                            disabled={busyAction !== null}
                            title='Release (remove) this DHCP lease on the router'
                        >
                            <FaUnlock size={12} /> {busyAction === 'release' ? 'Working…' : 'Release'}
                        </button>
                    )}
                    {!canAct && actionItemId && network.leaseId && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            disabled
                            title={routerStaleReason || 'Router unavailable.'}
                        >
                            <FaUnlock size={12} /> Release
                        </button>
                    )}
                    {actionItemId && propertyMismatch && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            onClick={handleApplyProperty}
                            disabled={busyAction !== null}
                            title="Set this ticket's property to the matched router's property"
                        >
                            <FaBuilding size={12} /> {busyAction === 'apply-property' ? 'Working…' : 'Apply Router Property'}
                        </button>
                    )}
                    {actionItemId && network.routerPropertyId && (
                        <button
                            type='button'
                            className='diag-action-btn'
                            onClick={handleCreateFollowUp}
                            title='Create a follow-up IT ticket pre-filled with this router location'
                        >
                            <FaPlusSquare size={12} /> Follow-up Ticket
                        </button>
                    )}
                    {actionItemId && network.routerId && (
                        <a
                            href={`/it/config-management/routers/${network.routerId}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='diag-action-btn'
                            title="Open this router's config page in CapTrac (new tab)"
                        >
                            <FaExternalLinkAlt size={13} />
                            Router Config Page
                        </a>
                    )}
                    {network.routerConfigUrl && (
                        <a
                            href={network.routerConfigUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='diag-action-btn diag-action-primary'
                            title='Open router WebFig configuration'
                        >
                            <FaExternalLinkAlt size={13} />
                            WebFig
                        </a>
                    )}
                </div>
            </div>

            {!routerOnline && routerStaleReason && (
                <div
                    className='diag-hint'
                    style={{
                        margin: '8px 0 12px',
                        padding: '8px 10px',
                        border: '1px solid #fbbf24',
                        background: '#fffbeb',
                        borderRadius: 4,
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '0.75rem'
                    }}
                >
                    <FaExclamationTriangle size={13} /> {routerStaleReason}
                </div>
            )}

            <div className='diag-grid'>
                {/* DHCP Lease Match */}
                <div className='diag-card diag-card-highlight'>
                    <div className='diag-card-title'><FaWifi size={13} /> Matched DHCP Lease</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>IP Address</span>
                            <span className='diag-value diag-value-mono'>{network.leaseAddress}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>MAC Address</span>
                            <span className='diag-value diag-value-mono'>{network.leaseMacAddress}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Hostname</span>
                            <span className='diag-value'>{network.leaseHostName || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>DHCP Server</span>
                            <span className='diag-value'>{network.leaseServer || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Status</span>
                            <span className='diag-badge' style={{ color: leaseStatus.color }}>
                                {leaseStatus.label}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Expires</span>
                            <span className='diag-value'>{network.leaseExpiresAfter || 'N/A'}</span>
                        </div>
                        {network.leaseComment && (
                            <div className='diag-row'>
                                <span className='diag-label'>Comment</span>
                                <span className='diag-value'>{network.leaseComment}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Router Details */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaServer size={13} /> Router Details</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>Management IP</span>
                            <span className='diag-value diag-value-mono'>{network.routerIpAddress}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>API Port</span>
                            <span className='diag-value'>{network.routerApiPort}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>RouterOS</span>
                            <span className='diag-value'>{network.routerOSVersion || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Mgmt Interface</span>
                            <span className='diag-value'>{network.managementInterfaceType || 'Direct'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Status</span>
                            <span className={`diag-badge ${network.provisioningStatus === 'Provisioned' ? 'diag-badge-green' : 'diag-badge-orange'}`}>
                                {network.provisioningStatus}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'><FaClock size={11} /> Last Connected</span>
                            <span className='diag-value diag-value-small'>{formatDate(network.lastConnectionSuccess)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* All DHCP Leases on this router (collapsible) */}
            {network.allLeases && network.allLeases.length > 0 && (
                <div className='diag-leases-section'>
                    <button
                        className='diag-leases-toggle'
                        onClick={() => setShowAllLeases(!showAllLeases)}
                    >
                        {showAllLeases ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        All DHCP Leases on {network.routerDisplayName}
                        <span className='diag-leases-count'>{network.allLeases.length}</span>
                    </button>

                    {showAllLeases && (
                        <div className='diag-leases-content'>
                            <div className='diag-leases-search'>
                                <FaSearch size={12} />
                                <input
                                    type='text'
                                    placeholder='Filter leases by IP, MAC, or hostname...'
                                    value={leaseSearch}
                                    onChange={(e) => setLeaseSearch(e.target.value)}
                                />
                            </div>
                            <div className='diag-leases-table-wrap'>
                                <table className='diag-leases-table'>
                                    <thead>
                                        <tr>
                                            <th>IP Address</th>
                                            <th>MAC Address</th>
                                            <th>Hostname</th>
                                            <th>Status</th>
                                            <th>Server</th>
                                            <th>Expires</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLeases.map((lease, idx) => {
                                            const isMatch = lease.macAddress === network.leaseMacAddress
                                                || lease.address === network.leaseAddress;
                                            return (
                                                <tr key={idx} className={isMatch ? 'diag-lease-highlight' : ''}>
                                                    <td className='diag-mono'>{lease.address}</td>
                                                    <td className='diag-mono'>{lease.macAddress}</td>
                                                    <td>{lease.hostName || '—'}</td>
                                                    <td>
                                                        <span className={`diag-lease-status ${lease.status === 'bound' ? 'bound' : ''}`}>
                                                            {lease.status}
                                                        </span>
                                                    </td>
                                                    <td>{lease.server}</td>
                                                    <td>{lease.expiresAfter || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                        {filteredLeases.length === 0 && (
                                            <tr><td colSpan={6} className='diag-empty-inline'>No matching leases found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Recent RouterMonitoring alerts for the same property — gives engineers
                instant context on whether this IT ticket correlates with an outage. */}
            {actionItemId && network.routerPropertyId && (
                <div className='diag-leases-section'>
                    <button
                        className='diag-leases-toggle'
                        onClick={() => setShowAlerts(!showAlerts)}
                    >
                        {showAlerts ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                        <FaBell size={12} style={{ marginRight: 4 }} />
                        Recent Router Alerts at this property
                        {recentAlerts !== null && (
                            <span className='diag-leases-count'>{recentAlerts.length}</span>
                        )}
                    </button>
                    {showAlerts && (
                        <div className='diag-leases-content'>
                            {loadingAlerts && (
                                <div className='diag-empty-inline'>Loading recent alerts…</div>
                            )}
                            {!loadingAlerts && recentAlerts && recentAlerts.length === 0 && (
                                <div className='diag-empty-inline'>
                                    No RouterMonitoring alerts for this property in the last 30 days.
                                </div>
                            )}
                            {!loadingAlerts && recentAlerts && recentAlerts.length > 0 && (
                                <div className='diag-leases-table-wrap'>
                                    <table className='diag-leases-table'>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Priority</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentAlerts.map(a => (
                                                <tr
                                                    key={a.id}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/action-items/${a.id}`)}
                                                    title='Open this alert'
                                                >
                                                    <td className='diag-mono'>#{a.id}</td>
                                                    <td>{a.title || '—'}</td>
                                                    <td>{a.status}</td>
                                                    <td>{a.priority}</td>
                                                    <td className='diag-value-small'>{formatDate(a.createdDate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NetworkDiagnosticsTab;